import api from "../api";
import endpoints from "../endpoints";
import moment from "moment";
import _ from "lodash";
import emitter from "tiny-emitter/instance";

function makeID(length) {
  let result = "";
  const characters = "c5JRE4o3CnKqX8mBhdzkl7bvSwWVNDyUMT0jeaiHIsPu62QGgLfOrZY1Ap9xtF";
  const charactersLength = characters.length;
  for (let i = 0; i < length - 6; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result + moment().format("YYMMDD");
}

class Logger {
  constructor() {
    this.logs = [];
    this.levels = { info: 0, warn: 1, error: 2 };
    const level = window.localStorage ? parseInt(window.localStorage.getItem("mcloud-loglevel")) : null;
    if (!isNaN(level) && level >= 0 && level <= 2) {
      this.loglevel = level;
    } else {
      this.loglevel = 2;
    }

    this._emptyRunTime = 0;
    this._scheduleWrite();
    this._isAuthenticatedSession = false;
    emitter.on("logged-in", () => {
      this._isAuthenticatedSession = true;
      if (!this._handle) {
        this._scheduleWrite();
      }
    });
    emitter.on("logged-out", () => {
      this._isAuthenticatedSession = false;
      if (this._handle) {
        clearTimeout(this._handle);
        this._handle = null;
      }
    });
  }

  info(name, msg, metadata) {
    return this.log("info", name, msg, metadata);
  }

  error(name, msg, metadata) {
    return this.log("error", name, msg, metadata);
  }

  warn(name, msg, metadata) {
    return this.log("warn", name, msg, metadata);
  }

  log(severity, name, msg, metadata) {
    if (this.loglevel <= this.levels[severity]) {
      metadata = metadata || {};
      Object.keys(metadata).forEach((key) => {
        metadata[key] = this.cleanObject(metadata[key]);
      });

      const event = {
        name: name,
        severity: severity,
        message: msg,
        eventTime: new Date().toISOString(),
        correlationId: makeID(25),
        metadata: metadata
      };
      this.logs.push(event);
    }
    return this.consoleOverride(severity, msg);
  }

  changeLevel(level) {
    this.loglevel = level;
    if (window.localStorage)
      try {
        window.localStorage.setItem("mcloud-loglevel", this.loglevel);
      } catch (error) {
        error("changeLogLevel", "Updating log level to localstorage resulted in error", { loglevel: this.loglevel, exception: error });
      }
  }

  logAll(events) {
    this.logs.push(...events);
  }

  beginContext(name) {
    return new ContextLogger(name, makeID(25), this);
  }

  async GetLogsFor(date) {
    try {
      const logs = await api.get(endpoints.logging.browse, {
        params: { date: date }
      });
      return logs.data;
    } catch (error) {
      console.error("logger failed to log to api", error);
    }
  }

  cleanObject(error) {
    if (error && error.isAxiosError) {
      const data = {
        info: error.toJSON(),
        response: error.response ? error.response.data : null
      };
      data.info.config = _.pick(data.info.config, ["baseURL", "data", "method", "url", "message"]);
      return JSON.stringify(data);
    } else if (error && typeof error !== "string" && typeof error !== "number") {
      if (error.toISOString) return error.toISOString();
      else return JSON.stringify(error);
    }
    return error;
  }

  async persist() {
    const logs = [...this.logs];
    try {
      await api.post(endpoints.logging.events, logs);
      this.logs = [];
    } catch (error) {
      console.error("logger failed to log to api", error);
    }
  }

  async _update() {
    if (this.disabled) clearTimeout(this._handle);
    else if (!this._isAuthenticatedSession) {
      return;
    } else if (this.logs.length > 0) {
      console.log(`Syncing ${this.logs.length} event logs...`);
      await this.persist();
    } else {
      if (this._emptyRunTime >= 60) {
        console.log("No logs to sync in last 1 minute. Current log level:" + this.loglevel, this.levels);
        this._emptyRunTime = 0;
      } else {
        this._emptyRunTime += 10;
      }
    }

    this._scheduleWrite();
  }

  _scheduleWrite() {
    if (!this._isAuthenticatedSession) {
      return;
    }
    this._handle = setTimeout(() => {
      this._update();
    }, 10000);
  }

  clearLogs() {
    this.logs = [];
  }

  consoleOverride(method, args) {
    return {
      alsoToConsole: console[method].bind(console, ...args),
      andToConsole: console[method].bind(console)
    };
  }
}

class ContextLogger {
  constructor(name, correlationId, logger) {
    this.name = name;
    this.correlationId = correlationId;
    this.logger = logger;
    this.logs = [];
    console.group(name);
    this._runningTime = 0;
    this._longRunningAlertHandle = setInterval(() => {
      this._runningTime += 12;
      console.log(
        `A log context (${this.name}) with ${this.logs.length} has been running for more than ${this._runningTime} seconds. Make sure you have called logContext.complete().`
      );
    }, 12000);
  }

  info(msg, metadata) {
    return this.log("info", msg, metadata);
  }

  error(msg, metadata) {
    return this.log("error", msg, metadata);
  }

  warn(msg, metadata) {
    return this.log("warn", msg, metadata);
  }

  log(severity, msg, metadata) {
    if (this.logger.loglevel <= this.logger.levels[severity]) {
      metadata = metadata || {};
      Object.keys(metadata).forEach((key) => {
        metadata[key] = this.logger.cleanObject(metadata[key]);
      });

      this.logs.push({
        name: this.name,
        severity: severity,
        message: msg,
        eventTime: new Date().toISOString(),
        correlationId: this.correlationId,
        metadata: metadata
      });
    }
    return this.logger.consoleOverride(severity, msg);
  }

  complete() {
    window.clearInterval(this._longRunningAlertHandle);
    this.logger.logAll(this.logs);
    this.logs = [];
    console.groupEnd();
  }
}

const logger = new Logger();

export default logger;
